import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

/**
 * Service dédié à tout ce qui concerne les contacts (controller Contact + liste CRM).
 * Association contact ↔ sociétés, contact ↔ rôles, création, liaison, liste globale.
 */
@Injectable({
  providedIn: 'root',
})
export class ContactService {
  private serverUrl = environment.serverUrl;
  private httpOptions = {
    headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
  };

  private contactsSignal = signal<any[]>([]);
  private contactsLoadingSignal = signal<boolean>(false);

  constructor(private http: HttpClient) { }

  get_contact_by_corporate_id(id) {
    return this.http.get<[]>(this.serverUrl + 'contact/get_contact_by_corporate_id/' + id, this.httpOptions)
      .pipe(catchError(this.handleError));
  }

  //@Cacheable({ cacheBusterObserver: CrmService.cacheBuster$ })
  get_crm_appointment_list_afs_participant(slug) {
    return this.http.get<[]>(this.serverUrl + 'contact/get_crm_appointment_list_afs_participant/' + slug, this.httpOptions)
      .pipe(
        catchError(this.handleError)
      );
  }

  get_crm_opportunity_contact_detail_by_slug_table(slug) {
    return this.http.get<[]>(this.serverUrl + 'contact/get_crm_opportunity_contact_detail_by_slug_table/' + slug, this.httpOptions)
      .pipe(
        catchError(this.handleError)
      );

  }

  /**
   * Détail d'un contact par son ID (avec interactions).
   */
  get_corporate_contact_details_by_id(contact_id: number): Observable<any> {
    return this.http
      .get<any>(this.serverUrl + 'contact/get_corporate_contact_details_by_id/' + contact_id, this.httpOptions)
      .pipe(catchError(this.handleError));
  }

  get_contact_by_slug(userpost: any) {
    return this.http.post<any>(this.serverUrl + 'contact/get_contact_by_slug', userpost, this.httpOptions)
      .pipe(
        catchError(this.handleError)
      );
  }

  get_contact_by_email(userpost: any) {
    return this.http.post<any>(this.serverUrl + 'contact/get_contact_by_email', userpost, this.httpOptions)
      .pipe(
        catchError(this.handleError)
      );
  }

  //@Cacheable({ cacheBusterObserver: CrmService.cacheBuster$ })
  get_crm_opportunity_sponsor_contact_detail_by_slug(slug) {
    return this.http.get<[]>(this.serverUrl + 'contact/get_crm_opportunity_sponsor_contact_detail_by_slug/' + slug, this.httpOptions)
      .pipe(
        catchError(this.handleError)
      );
  }
  //@Cacheable({ cacheBusterObserver: CrmService.cacheBuster$ })
  get_crm_opportunity_appointment_contact(slug) {
    return this.http.get<[]>(this.serverUrl + 'contact/get_crm_opportunity_appointment_contact/' + slug, this.httpOptions)
      .pipe(
        catchError(this.handleError)
      );
  }
  //@Cacheable({ cacheBusterObserver: CrmService.cacheBuster$ })
  get_crm_billinginfocorpo_contact(slug) {
    return this.http.get<[]>(this.serverUrl + 'contact/get_crm_billinginfocorpo_contact/' + slug, this.httpOptions)
      .pipe(
        catchError(this.handleError)
      );
  }
  //@Cacheable({ cacheBusterObserver: CrmService.cacheBuster$ })
  get_crm_opportunity_appointment_sponsor_contact(slug) {
    return this.http.get<[]>(this.serverUrl + 'crm_appointment/get_crm_opportunity_appointment_sponsor_contact/' + slug, this.httpOptions)
      .pipe(
        catchError(this.handleError)
      );
  }

  get_crm_opportunity_contact_detail_by_slug(slug) {
    return this.http.get<[]>(this.serverUrl + 'contact/get_crm_opportunity_contact_detail_by_slug/' + slug, this.httpOptions)
      .pipe(
        catchError(this.handleError)
      );
  }

  removeBillingContact(crm_billing_corporate_contact_id: number): Observable<any> {
    return this.http.post<any>(this.serverUrl + 'contact/removeBillingContact', { crm_billing_corporate_contact_id }, this.httpOptions)
      .pipe(
        catchError(this.handleError),
        tap(() => {
          this.get_all_contacts();
        })
      );
  }

  /** Liste des contacts (signal en lecture seule). */
  getAllContactsSignal() {
    return this.contactsSignal.asReadonly();
  }

  /** État de chargement des contacts. */
  getStatus() {
    return this.contactsLoadingSignal.asReadonly();
  }

  /** Charge tous les contacts (CRM) et met à jour le signal. */
  get_all_contacts(): void {
    this.contactsLoadingSignal.set(true);
    this.http
      .get<any[]>(
        this.serverUrl + 'crm/get_all_coporate_contact',
        this.httpOptions
      )
      .pipe(catchError(this.handleError))
      .subscribe({
        next: (contacts) => {
          this.contactsSignal.set(contacts ?? []);
          this.contactsLoadingSignal.set(false);
        },
        error: () => {
          this.contactsLoadingSignal.set(false);
        },
      });
  }

  /** Liste paginée des contacts actifs (table All contacts). */
  get_all_coporate_contact_paginated(dataTablesParameters: any): Observable<any> {
    return this.http
      .post<any>(
        this.serverUrl + 'contact/get_all_coporate_contact_paginated',
        dataTablesParameters,
        this.httpOptions
      )
      .pipe(catchError(this.handleError));
  }

  /** Liste paginée des contacts supprimés (admin uniquement). Même format de requête que get_all_coporate_contact_paginated. */
  get_all_deleted_contact_paginated(dataTablesParameters: any): Observable<any> {
    return this.http
      .post<any>(
        this.serverUrl + 'contact/get_all_deleted_contact_paginated',
        dataTablesParameters,
        this.httpOptions
      )
      .pipe(catchError(this.handleError));
  }

  /**
   * Vérifie les liaisons contact-corporate par email (ex: formulaire ancien contact).
   */
  check_contact_corporate_by_email(request: any): Observable<any> {
    return this.http
      .post<any>(
        this.serverUrl + 'contact/check_contact_corporate_by_email',
        request,
        this.httpOptions
      )
      .pipe(catchError(this.handleError));
  }

  /**
   * Retourne la liste des sociétés liées à un contact (par slug du contact).
   */
  get_corporates_by_contact_id(contact_id: number): Observable<any> {
    return this.http
      .get<any>(
        this.serverUrl + 'contact/get_corporates_by_contact_id/' + contact_id,
        this.httpOptions
      )
      .pipe(catchError(this.handleError));
  }

  /** Retourne les rôles de distribution list (distribution_list_role). */
  get_distribution_list_roles(): Observable<any> {
    return this.http
      .get<any>(this.serverUrl + 'contact/get_distribution_list_roles', this.httpOptions)
      .pipe(catchError(this.handleError));
  }

  /** Ajoute des contacts à la distribution list d'un investor (tx_investors.id). */
  add_investor_distribution_list_contacts(
    tx_investors_id: number,
    items: { contact_corporate_id: number; distribution_list_role_ids: number[] }[]
  ): Observable<any> {
    return this.http
      .post<any>(
        this.serverUrl + 'contact/add_investor_distribution_list_contacts',
        { tx_investors_id, items },
        this.httpOptions
      )
      .pipe(catchError(this.handleError));
  }

  /** Met à jour les rôles de distribution list pour un contact existant. */
  update_investor_distribution_list_contact_roles(
    investor_distribution_list_contact_id: number,
    distribution_list_role_ids: number[]
  ): Observable<any> {
    return this.http
      .post<any>(
        this.serverUrl + 'contact/update_investor_distribution_list_contact_roles',
        { investor_distribution_list_contact_id, distribution_list_role_ids },
        this.httpOptions
      )
      .pipe(catchError(this.handleError));
  }

  /** Supprime un contact de la distribution list d'un investor. */
  remove_investor_distribution_list_contact(tx_investors_id: number, contact_corporate_id: number): Observable<any> {
    return this.http
      .post<any>(
        this.serverUrl + 'contact/remove_investor_distribution_list_contact',
        { tx_investors_id, contact_corporate_id },
        this.httpOptions
      )
      .pipe(catchError(this.handleError));
  }

  /**
   * Restaure une liaison contact_corporate (is_deleted = 0, reason = ''). Admin uniquement.
   */
  /**
   * Restaure la liaison contact-société (is_deleted = 0).
   * Body optionnel : { fonction?, role_ids? } pour appliquer fonction et rôles à la liaison restaurée.
   */
  restore_contact_on_company(
    contact_id: number,
    corporate_id: number,
    body?: { fonction?: string; role_ids?: number[] }
  ): Observable<any> {
    return this.http
      .post<any>(
        this.serverUrl + 'contact/restore_contact_on_company/' + contact_id + '/' + corporate_id,
        body ?? {},
        this.httpOptions
      )
      .pipe(catchError(this.handleError));
  }

  /**
   * Soft delete : met is_deleted à 1 pour la liaison contact_corporate (IDs uniquement).
   * POST body : { reason?, contact_name?, corporate_name?, url? }
   */
  delete_contact_on_company(contact_id: number, corporate_id: number, body?: Record<string, unknown>): Observable<any> {
    return this.http
      .post<any>(
        this.serverUrl + 'contact/delete_contact_on_company/' + contact_id + '/' + corporate_id,
        body || {},
        this.httpOptions
      )
      .pipe(catchError(this.handleError));
  }

  /**
   * Retourne la liste des rôles contact (contact_role).
   */
  get_contact_roles(): Observable<any> {
    return this.http
      .get<any>(this.serverUrl + 'contact/get_contact_roles', this.httpOptions)
      .pipe(catchError(this.handleError));
  }

  /**
   * Retourne le nombre de contacts ayant le rôle "Billing contact" pour une société.
   * Si contact_id est fourni, retourne aussi is_contact_billing (ce contact a-t-il le rôle).
   */
  get_billing_contact_count_for_corporate(
    corporate_id: number,
    contact_id?: number
  ): Observable<{ status: string; count: number; is_contact_billing?: boolean }> {
    const params: any = { corporate_id };
    if (contact_id != null) params['contact_id'] = contact_id;
    return this.http
      .get<{ status: string; count: number; is_contact_billing?: boolean }>(
        this.serverUrl + 'contact/get_billing_contact_count_for_corporate',
        { ...this.httpOptions, params }
      )
      .pipe(catchError(this.handleError));
  }

  /**
   * Lie un contact existant à une corporate (avec rôles optionnels).
   */
  link_contact_to_corporate(
    contact_id: number,
    corporate_id: number,
    role_ids?: number[]
  ): Observable<any> {
    return this.http
      .post<any>(
        this.serverUrl + 'contact/link_contact_to_corporate',
        { contact_id, corporate_id, role_ids: role_ids || [] },
        this.httpOptions
      )
      .pipe(catchError(this.handleError));
  }


  /**
   * Assigns the "Billing contact" role at company level via update_contact + role_ids.
   * Skips when the corporate already has a billing contact.
   */
  promoteContactAsBillingContactForCorporate(
    contact: any,
    corporateId: number
  ): Observable<{ status: string; skipped?: boolean; message?: string }> {
    const contactId = Number(contact?.id ?? contact?.contact_id);
    const parsedCorporateId = Number(corporateId);
    if (!contactId || parsedCorporateId <= 0) {
      return throwError(() => new Error('Invalid contact or corporate id'));
    }

    return forkJoin({
      billingCount: this.get_billing_contact_count_for_corporate(parsedCorporateId),
      contactRoles: this.get_contact_roles(),
      corporates: this.get_corporates_by_contact_id(contactId),
    }).pipe(
      switchMap(({ billingCount, contactRoles, corporates }) => {
        if ((billingCount?.count ?? 0) > 0) {
          return of({ status: 'skipped' });
        }

        const rolesList = contactRoles?.data ?? [];
        const billingRole = Array.isArray(rolesList)
          ? rolesList.find((role: any) => /billing\s*contact|payment\s*agent/i.test(role?.name || ''))
          : null;
        const billingRoleId = billingRole?.id != null ? Number(billingRole.id) : null;
        if (!billingRoleId) {
          return throwError(() => new Error('Billing contact role id not found'));
        }

        const corporatesList = corporates?.data ?? [];
        const companyLink = Array.isArray(corporatesList)
          ? corporatesList.find((row: any) => Number(row.corporate_id) === parsedCorporateId)
          : null;
        const existingRoleIds = (companyLink?.roles ?? [])
          .map((role: any) => Number(role.id))
          .filter((roleId: number) => !isNaN(roleId) && roleId > 0);
        const roleIds = Array.from(new Set([...existingRoleIds, billingRoleId]));

        const payload = this.buildBillingContactUpdatePayload(contact, parsedCorporateId, roleIds);
        return this.update_contact(contactId, payload);
      }),
      catchError(this.handleError)
    );
  }

  private buildBillingContactUpdatePayload(
    contact: any,
    corporateId: number,
    roleIds: number[]
  ): any {
    return {
      generic_contact: contact.generic_contact === true || contact.generic_contact === 1,
      name: contact.name ?? '',
      forename: contact.forename ?? '',
      birthdate: contact.birthdate ?? null,
      geography: contact.geography ?? contact.country ?? '',
      comments: contact.comments ?? '',
      corporate_id: corporateId,
      directline: contact.directline ?? '',
      email: contact.email ?? '',
      fonction: contact.fonction ?? contact.title ?? '',
      indicatif_directline: contact.indicatif_directline ?? '+33',
      indicatif_phone: contact.indicatif_phone ?? '+33',
      phone: contact.phone ?? '',
      role_ids: roleIds,
      specialty: contact.specialty ?? '',
      title: contact.title ?? '',
    };
  }

  /**
   * Vérifie si un contact existe avec cet email (sans filtrer par corporate).
   */
  check_contact_by_email(request: any): Observable<any> {
    return this.http
      .post<any>(
        this.serverUrl + 'contact/check_contact_by_email',
        request,
        this.httpOptions
      )
      .pipe(catchError(this.handleError));
  }

  /**
   * Met à jour un contact (nom, prénom, email, téléphones, etc.). S'applique à toutes les sociétés liées.
   */
  update_contact(contactId: number, payload: any): Observable<any> {
    return this.http
      .post<any>(
        this.serverUrl + 'contact/update_contact/' + contactId,
        payload,
        this.httpOptions
      )
      .pipe(
        catchError(this.handleError),
        tap(() => {
          this.get_all_contacts();
        })
      );
  }

  /**
   * Délie un contact d'une société (supprime la liaison contact_corporate).
   * Utilisé quand on "sépare" un contact en créant un nouveau contact pour une société avec un nouvel email.
   */
  unlink_contact_from_corporate(contact_id: number, corporate_id: number): Observable<any> {
    return this.http
      .post<any>(
        this.serverUrl + 'contact/unlink_contact_from_corporate',
        { contact_id, corporate_id },
        this.httpOptions
      )
      .pipe(catchError(this.handleError));
  }

  /**
   * Lie un contact existant à une corporate avec une fonction et des rôles.
   */
  link_contact_to_corporate_with_function(
    contact_id: number,
    corporate_id: number,
    role_ids: number[],
    fonction: string
  ): Observable<any> {
    return this.http
      .post<any>(
        this.serverUrl + 'contact/link_contact_to_corporate_with_function',
        { contact_id, corporate_id, role_ids: role_ids || [], fonction },
        this.httpOptions
      )
      .pipe(catchError(this.handleError));
  }

  /**
   * Ancien flux : ajoute un nouveau contact (1 corporate, role legacy).
   * En cas de succès, rafraîchit la liste des contacts.
   */
  add_new_contact(contactpost: any): Observable<any> {
    return this.http
      .post<any>(
        this.serverUrl + 'contact/add_new_contact',
        contactpost,
        this.httpOptions
      )
      .pipe(
        catchError(this.handleError),
        tap((contact: any) => {
          if (contact?.status === 'success') {
            this.get_all_contacts();
          }
        })
      );
  }

  /**
   * Ajoute un nouveau contact avec rôles (stepper 3 étapes).
   * En cas de succès, rafraîchit la liste des contacts.
   */
  add_new_contact_with_roles(contactpost: any): Observable<any> {
    return this.http
      .post<any>(
        this.serverUrl + 'contact/add_new_contact_with_roles',
        contactpost,
        this.httpOptions
      )
      .pipe(
        catchError(this.handleError),
        tap((contact: any) => {
          if (contact?.status === 'success') {
            this.get_all_contacts();
          }
        })
      );
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    if (error.error instanceof ErrorEvent) {
      console.error('An error occurred:', error.error.message);
    } else {
      console.error(
        `Backend returned code ${error.status}, body was: ${error.message}`
      );
    }
    return throwError(() => 'Something bad happened. Please try again later.');
  }

  //@CacheBuster({ cacheBusterNotifier: CrmService.cacheBuster$ })
  update_crm_opportunity_contact(opportunitypost: any, slug) {
    return this.http.post<any>(this.serverUrl + 'contact/update_crm_opportunity_contact/' + slug, opportunitypost, this.httpOptions)
      .pipe(catchError(this.handleError));
  }

  /** Get contact roles in a deal, optionally filtered by corporate associate role (associate_role_id). */
  get_role_contact_deal(associate_role_id?: number) {
    const options: any = { ...this.httpOptions };
    if (associate_role_id != null) {
      options.params = { associate_role_id: associate_role_id.toString() };
    }
    return this.http.get<any>(this.serverUrl + 'contact/get_role_contact_deal', options)
      .pipe(
        catchError(this.handleError)
      );
  }

  get_crm_opportunity_contacts(deal_id) {
    return this.http.get<[]>(this.serverUrl + 'contact/get_crm_opportunity_contacts/' + deal_id, this.httpOptions)
      .pipe(catchError(this.handleError));
  }

  remove_crm_opportunity_contact(request: any) {
    return this.http.post<any>(this.serverUrl + 'contact/remove_crm_opportunity_contact', request, this.httpOptions)
      .pipe(catchError(this.handleError));
  }

  //@CacheBuster({ cacheBusterNotifier: DealsService.cacheBuster$ })
  searchContact(any: any) {
    return this.http.post<any>(this.serverUrl + 'contact/searchContact', any, this.httpOptions)
      .pipe(
        catchError(this.handleError)
      );
  }
}
